import React, {
  type FC,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type ReactNode,
  type PropsWithChildren,
  forwardRef,
  useEffect,
  useRef,
  useState,
  createContext,
  Children,
  isValidElement,
  type HTMLAttributes,
} from 'react';

import gsap from 'gsap';
import clsx from 'clsx';
import { assignRefs } from '@lucid/utils';
import { theme } from '@lucid/theme';

import CardDivider, { type CardDividerProps } from './subcomponents/divider';
import CardHeading, { type CardHeadingProps } from './subcomponents/heading';
import CardLabel, { type CardLabelProps } from './subcomponents/label';
import CardLink, { type CardLinkProps } from './subcomponents/link';
import CardMedia, { type CardMediaProps } from './subcomponents/media';
import CardTags, { type CardTagsProps } from './subcomponents/tags';
import CardText, { type CardTextProps } from './subcomponents/text';
import decorate from '../decorate/decorate';
import Box from '../box/box';

export interface CardSubComponents {
  /** The Subtitle Subcomponent for Card. */
  Divider: FC<PropsWithChildren<CardDividerProps>>;
  /** The Title Subcomponent for Card. */
  Heading: FC<PropsWithChildren<CardHeadingProps>>;
  /** The Title Subcomponent for Card. */
  Label: FC<PropsWithChildren<CardLabelProps>>;
  /** The Title Subcomponent for Card. */
  Link: FC<PropsWithChildren<CardLinkProps>>;
  /** The Title Subcomponent for Card. */
  Media: FC<PropsWithChildren<CardMediaProps>>;
  /** The Title Subcomponent for Card. */
  Tags: FC<PropsWithChildren<CardTagsProps>>;
  /** The Title Subcomponent for Card. */
  Text: FC<PropsWithChildren<CardTextProps>>;
}

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The Card component children. */
  children?: ReactNode;
  /** Determines whether the Card has an 'elevated' or 'flat' appearance */
  shadow?: 'default' | 'hover' | 'active' | 'never';
  /** Enables hover animation. */
  shouldAnimateOnHover?: boolean;
  /** Will truncate the Card text elements */
  shouldTruncateText?: boolean;
  /** Determines whether the card is in the active state */
  isActiveCard?: boolean;
  /** Determines whether the entire card should act as a link instead of displaying a link */
  isFullCardLink?: boolean;
  /** Is the link navigating to the template hub */
  isTemplateHubLink?: boolean;
};

export interface CardElementProps {
  /** The Card Heading */
  heading?: string;
  /** The Card Text */
  text?: CardTextProps;
  /** The Card Image */
  media?: CardMediaProps;
  /** The Card Link */
  link?: CardLinkProps;
  /** The Card label */
  label?: CardLabelProps;
  /** The tags */
  tags?: CardTagsProps;
  /** The divider */
  divider?: CardDividerProps;
  /** The children passed to the Card component. */
  children?: ReactNode;
}

interface CardContextType {
  isFullCardLink: boolean;
  isActiveCard?: boolean;
  hasLinkChild?: boolean;
  setHasLinkChild?: (value: boolean) => void;
}

export const CardContext = createContext<CardContextType>({
  isFullCardLink: false,
  isActiveCard: false,
  hasLinkChild: false,
  setHasLinkChild: () => {},
});

const CardComponent: ForwardRefExoticComponent<PropsWithoutRef<CardProps>> & Partial<CardSubComponents> = forwardRef<
  HTMLDivElement,
  CardProps
>(({ children, className, isActiveCard, shouldAnimateOnHover = false, shadow = 'default', ...rest }, forwardedRef) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const [hasHover, setHasHover] = useState(false);
  const [hasLinkChild, setHasLinkChild] = useState(false);
  const hasFullBleedMediaChild = Children.toArray(children).some(
    child => isValidElement(child) && child.props.isFullBleed,
  );

  useEffect(() => {
    if (hasLinkChild && shouldAnimateOnHover && animationRef.current) {
      hasHover
        ? gsap.to(animationRef.current, {
            translateY: '-16px',
            zIndex: theme.zIndices.z10,
            duration: 0.15,
            ease: '0.15s ease-out',
            overwrite: true,
          })
        : gsap.to(animationRef.current, {
            translateY: 0,
            zIndex: 0,
            duration: 0.15,
            ease: '0.15s ease-out',
            overwrite: true,
          });
    }
  }, [hasHover, shouldAnimateOnHover, hasLinkChild]);

  const defaultContext = {
    isFullCardLink: hasLinkChild,
    isActiveCard,
    hasLinkChild,
    setHasLinkChild,
  };

  return (
    <CardContext.Provider value={defaultContext}>
      <Box
        {...rest}
        ref={assignRefs(animationRef, forwardedRef)}
        css={theme => [
          {
            label: 'card',
            height: 'auto',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '120px',
            minWidth: '215px',
            gap: theme.space.s24,
            maxWidth: '520px',
            alignContent: 'start',
            padding: theme.space.s24,
            position: 'relative',
            border: `1px solid ${theme.colors.coolGrey[10]}`,
            '[data-card-element]': {
              margin: 0,
            },
            'a[data-card-element]:last-child': {
              marginTop: 'auto',
            },

            '&[data-card-element="carousel-card-container"]:focus-visible': {
              '&::after': {
                position: 'absolute',
                content: '""',
                height: 'calc(100% + 16px)',
                width: 'calc(100% + 16px)',
                top: '-8px',
                bottom: 0,
                left: '-8px',
                right: 0,
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${encodeURIComponent(`${theme.colors.primary}`)}' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              },
            },
            '&.fullBleedMedia': {
              paddingTop: 0,
              '[data-card-element="media"]': {
                marginBottom: `-${theme.space.s24}px`,
                overflow: 'hidden',
              },
            },
          },
          {
            boxShadow: theme.shadows.cardDefault,
            transition: 'box-shadow 0.15s ease',
          },
          isActiveCard && {
            outline: `2px solid ${theme.colors.productColor({
              co: theme.colors.blue[70],
              chart: theme.colors.orange[70],
              spark: theme.colors.red[70],
              edu: theme.colors.blue[70],
              chartblog: theme.colors.orange[70],
            })}`,
            outlineOffset: '-1px',
          },
          shouldAnimateOnHover &&
            hasLinkChild && {
              '&:hover': {
                boxShadow: 'none',
              },
              '&:active, &.isActive': {
                outline: '2px solid',
                outlineColor: 'primary',
                outlineOffset: '-2px',
                boxShadow: theme.shadows.cardHover,
              },
            },
          shadow !== 'default' && {
            boxShadow: 'none',
          },
          shadow !== 'default' &&
            shouldAnimateOnHover &&
            hasLinkChild && {
              '&:hover': {
                boxShadow: shadow === 'hover' ? theme.shadows.cardHover : 'none',
              },
            },
        ]}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={clsx(className, hasFullBleedMediaChild && 'fullBleedMedia', isActiveCard && 'isActive')}
        onMouseEnter={() => setHasHover(true)}
        onMouseLeave={() => setHasHover(false)}
      >
        {children}
      </Box>
    </CardContext.Provider>
  );
});

CardComponent.Divider = CardDivider;
CardComponent.Heading = CardHeading;
CardComponent.Label = CardLabel;
CardComponent.Link = CardLink;
CardComponent.Media = CardMedia;
CardComponent.Tags = CardTags;
CardComponent.Text = CardText;

CardComponent.displayName = 'Card';
const Card = decorate<CardProps, CardSubComponents>(CardComponent);
export { Card };
export default Card;
